/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
import {
  useState,
  useEffect,
  useRef
} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useNavigate, useLocation} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSignOutAlt} from '@fortawesome/free-solid-svg-icons';

import userStore from 'services/user-store/';
import {IAppStore} from 'store';
import {getPreferredTimeFormat, logout} from 'store/action';
import text from './text';

import {SideMenu} from 'components/side-menu';
import urlRoutes from 'routers/url-routes';

import {useLoginStatus} from 'utility/custom-hooks';

import {
  TopNavigation,
  User,
  ApplicationId,
  UserInitials,
  MenuIcon,
  NavigationLeftSide
} from './style';

import logo from 'assets/images/phenix-logo-101x41.png';
import menuIcon from 'assets/images/icon/menu.svg';

const Header = (): JSX.Element => {
  const ref = useRef(null);
  const dispatch = useDispatch();
  const {search, pathname}: {search: string; pathname: string} = useLocation();
  const applicationId = useSelector((state: IAppStore) => state.user.applicationId);
  const [viewUserDetails, setViewUserDetails] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isLoggedIn] = useLoginStatus(applicationId);
  const navigate = useNavigate();
  const {phenixText} = text;
  let userInitials = '';

  if (applicationId) {
    userInitials = applicationId.substring(0, 2);
  }

  const handleClickOutside = event => {
    if (ref.current && !ref.current.contains(event.target)) {
      setViewUserDetails(false);
    }
  };

  const checkAndRedirect = async(): Promise<void> => {
    const applicationId = await userStore.get('applicationId');

    if (!applicationId) {
      return navigate(`${urlRoutes.login.path}/${search}`);
    }

    const lastVisitedRoutes = await userStore.get('lastVisitedRouteByApplicationId') || {};
    const lastVisitedRoute = lastVisitedRoutes[applicationId];
    const channelsPath = `/${urlRoutes.channels.path}/`;

    if (!pathname.includes(applicationId)) {
      if (lastVisitedRoute) {
        return navigate(`${lastVisitedRoute}${search}`);
      }

      return navigate(`${applicationId}${channelsPath}${search}`);
    }
  };

  const setLastVisitedRoute = async(): Promise<void> => {
    const applicationId = await userStore.get('applicationId');
    const lastVisitedRoutes = await userStore.get('lastVisitedRouteByApplicationId') || {};

    await userStore.set('lastVisitedRouteByApplicationId', {
      ...lastVisitedRoutes,
      [applicationId]: pathname
    });
  };

  useEffect(() => {
    dispatch(getPreferredTimeFormat());
  }, []);

  useEffect(() => {
    checkAndRedirect();
  }, [isLoggedIn]);

  useEffect(() => {
    setLastVisitedRoute();
  }, [pathname]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  const handleLogout = async(): Promise<void> => {
    dispatch(logout());
  };

  const handleCaretClick = () => setViewUserDetails(!viewUserDetails);

  return (
    <TopNavigation isLoggedIn={isLoggedIn}>
      <NavigationLeftSide>
        {(isLoggedIn && applicationId) &&
          <MenuIcon
            onClick={() => setShowMenu(!showMenu)}
            src={menuIcon}
            alt="menuIcon"
          />
        }
        <img src={logo} alt={phenixText} />
      </NavigationLeftSide>
      {(isLoggedIn && applicationId) &&
        <>
          <User ref={ref} onClick={handleCaretClick} className="user-info">
            <UserInitials>{userInitials}</UserInitials>
            <ApplicationId className="testId-applicationId">{applicationId}</ApplicationId>
            <div className="testId-logoutButton" onClick={handleLogout} onKeyDown={null} role="button" tabIndex={-10}>
              <FontAwesomeIcon icon={faSignOutAlt} size="lg" />
            </div>
          </User>
          {showMenu &&
            <SideMenu showMenu={() => setShowMenu(!showMenu)} />
          }
        </>
      }
    </TopNavigation>
  );
};

export default Header;